/**
 * Printable cheat sheet (§3, open question 3).
 *
 * The draft-night insurance policy: if the tool breaks, the venue wifi dies, or
 * your phone does, a sheet of paper still works. It is laid out by position in
 * columns rather than as one long overall list, because that is the layout you
 * can actually use while on the clock — you know you need a running back, and
 * you want to see the running backs.
 *
 * The dense, gridded, color-banded-tier look is a deliberate nod to the
 * original BeerSheets printouts (defunct since 2023, so this is built from
 * value-based-drafting cheat sheet conventions, not a pixel copy of a page
 * nobody can load anymore): tiers get a background band as well as a rule, so
 * a cliff is visible at a glance and not just at the moment you cross it.
 *
 * Deliberately dependency-free. Rendering to PDF is the browser's "Save as PDF"
 * on top of a print stylesheet, so there is no PDF library in the bundle, no
 * server round-trip, and it works with no network at all — which is the entire
 * point of a fallback.
 *
 * Attribution is printed on the sheet (R3). Whatever else a paper copy loses,
 * it does not get to lose the credit.
 */

import { el, clear, fmt } from './dom.ts';
import type { AppState } from '../state/store.ts';
import type { Position } from '../core/types.ts';
import { FANTASY_POSITIONS } from '../core/types.ts';
import type { BoardRow } from './board.ts';

export interface CheatSheetOptions {
  /** How many players to print per position. */
  perPosition: Partial<Record<Position, number>>;
  /** Include players already drafted (useful for a pre-draft printout). */
  includeDrafted: boolean;
  onClose: () => void;
}

/** How many distinct tier-band colors to cycle through per column before repeating. */
const TIER_PALETTE_SIZE = 10;

export const DEFAULT_CHEAT_SHEET_DEPTH: Record<Position, number> = {
  QB: 20,
  RB: 45,
  WR: 55,
  TE: 20,
  K: 10,
  DEF: 12,
};

export function renderCheatSheet(
  host: HTMLElement,
  state: AppState,
  rows: BoardRow[],
  opts: CheatSheetOptions,
): void {
  clear(host);

  const league = state.league;
  const engine = state.engine;

  const byPosition = new Map<Position, BoardRow[]>();
  for (const row of rows) {
    const pos = row.player.position;
    if (!pos) continue;
    if (!opts.includeDrafted && row.drafted) continue;
    const list = byPosition.get(pos) ?? [];
    list.push(row);
    byPosition.set(pos, list);
  }

  const sourceName =
    state.sources.find((s) => s.source_id === state.config.active_source_id)?.source_name ??
    (state.config.active_source_id === 'value' ? 'value over replacement (BeerSheets-style)' : 'blended ranking');

  const sheet = el('div', { class: 'cheatsheet' });

  // --- header --------------------------------------------------------------
  sheet.appendChild(
    el(
      'header',
      { class: 'cs-head' },
      el(
        'div',
        {},
        el('h1', {}, league?.name ?? 'Draft cheat sheet'),
        el(
          'p',
          { class: 'cs-sub' },
          [
            league ? `${league.teams}-team` : null,
            league ? `${league.rounds} rounds` : null,
            league?.draft_slot ? `pick ${league.draft_slot}` : null,
            `sorted by ${sourceName}`,
            engine ? `${engine.baselines.method} baseline` : null,
            `printed ${new Date().toLocaleDateString()}`,
          ]
            .filter(Boolean)
            .join(' · '),
        ),
      ),
      el(
        'div',
        { class: 'cs-actions no-print' },
        el(
          'button',
          { class: 'btn btn-primary', type: 'button', onClick: () => window.print() },
          'Print / Save as PDF',
        ),
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: opts.onClose }, 'Close'),
      ),
    ),
  );

  // --- the columns ---------------------------------------------------------
  const grid = el('div', { class: 'cs-grid' });

  for (const position of FANTASY_POSITIONS) {
    const list = byPosition.get(position) ?? [];
    if (list.length === 0) continue;

    const depth = opts.perPosition[position] ?? DEFAULT_CHEAT_SHEET_DEPTH[position];
    const column = el('section', { class: `cs-col cs-${position.toLowerCase()}` });
    column.appendChild(
      el(
        'h2',
        {},
        position,
        el('span', { class: 'cs-count' }, ` ${Math.min(depth, list.length)}`),
      ),
    );

    const table = el('table', { class: 'cs-table' });
    table.appendChild(
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          el('th', { class: 'cs-rank' }, '#'),
          el('th', { class: 'cs-name' }, 'Player'),
          el('th', { class: 'cs-team' }, 'Tm'),
          el('th', { class: 'cs-bye' }, 'Bye'),
          el('th', { class: 'cs-adp' }, 'ADP'),
          el('th', { class: 'cs-vor' }, 'VOR'),
        ),
      ),
    );
    const body = el('tbody');
    let lastTier: number | null = null;
    // Cycles independently of the tier number itself, which can start
    // anywhere or skip values — this just needs to alternate colors.
    let tierColorIndex = -1;

    list.slice(0, depth).forEach((row, i) => {
      // A visible rule between tiers is the single most useful thing on a paper
      // sheet: it turns "who is next" into "is the cliff before or after him" —
      // the background band underneath does the same job at a glance, before
      // you have even found the rule.
      if (row.tier !== null && row.tier !== lastTier) {
        if (lastTier !== null) {
          body.appendChild(
            el(
              'tr',
              { class: 'cs-tier-break' },
              el('td', { colspan: '6' }, `Tier ${row.tier}`),
            ),
          );
        }
        lastTier = row.tier;
        tierColorIndex++;
      }

      const tierClass = row.tier !== null ? `cs-tier-${tierColorIndex % TIER_PALETTE_SIZE}` : '';

      body.appendChild(
        el(
          'tr',
          { class: [tierClass, row.drafted ? 'cs-drafted' : ''].filter(Boolean).join(' ') },
          el('td', { class: 'cs-rank' }, String(i + 1)),
          el('td', { class: 'cs-name' }, row.player.full_name),
          el('td', { class: 'cs-team' }, row.player.team ?? ''),
          el('td', { class: 'cs-bye' }, row.player.bye_week ? String(row.player.bye_week) : ''),
          el('td', { class: 'cs-adp' }, row.adp ? fmt.num(row.adp.adp, 1) : ''),
          el(
            'td',
            { class: 'cs-vor' },
            row.valuation ? fmt.signed(row.valuation.vor, 0) : '',
          ),
        ),
      );
    });

    table.appendChild(body);
    column.appendChild(table);
    grid.appendChild(column);
  }

  sheet.appendChild(grid);

  // --- legend and attribution ---------------------------------------------
  sheet.appendChild(
    el(
      'footer',
      { class: 'cs-foot' },
      el(
        'p',
        { class: 'cs-legend' },
        'Columns: positional rank · player · team · bye · ADP (average draft position, so a rank far ahead of ADP is a value) · ' +
          'VOR (est.), points above a replacement-level player at that position. ' +
          'Shaded bands and rules mark tier breaks. Projections are estimated from rank, not a real projection source.',
      ),
      el(
        'p',
        { class: 'cs-attribution' },
        'Sources: ' +
          state.sources.map((s) => `${s.source_name} (${s.as_of_date})`).join(' · ') +
          (state.adp ? ` · ADP courtesy of Fantasy Football Calculator (${state.adp.as_of_date})` : ''),
      ),
    ),
  );

  host.appendChild(sheet);
}

/**
 * Rows for the sheet, ignoring the on-screen position filter.
 *
 * The board's filters are for working a live draft; a printed sheet wants every
 * position regardless of what happens to be filtered on screen. Reusing the
 * filtered rows here would silently print a one-position sheet.
 */
export function cheatSheetRows(state: AppState, allRows: BoardRow[]): BoardRow[] {
  return allRows;
}
